import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from '../types/chemistry';
import { chemistryApi } from '../api/chemistry';
import { useAuth } from '../context/AuthContext';
import { useUIStore } from '../stores/uiStore';
import ChemistryProjectCard from '../components/chemistry/ChemistryProjectCard';
import PageTransition from '../components/common/PageTransition';
import './ChemistryDashboard.css';

type Tab = 'active' | 'archived';

const ChemistryDashboard: React.FC = () => {
  console.log('🟢 ChemistryDashboard MOUNTED', window.location.pathname);
  const navigate = useNavigate();
  const { user } = useAuth();
  const showNotification = useUIStore((s) => s.showNotification);
  const isSuperAdmin = user?.user_type === 'super_admin' || user?.role === 'super_admin';

  const [projects, setProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('active');

  // Undo state
  const [undoItem, setUndoItem] = useState<{ project: Project; timer: ReturnType<typeof setTimeout> } | null>(null);
  const undoRef = useRef(undoItem);
  undoRef.current = undoItem;

  // Delete confirmation modal
  const [deleteConfirm, setDeleteConfirm] = useState<{ project: Project; confirmText: string } | null>(null);

  // Create project modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#10B981');
  const [isCreating, setIsCreating] = useState(false);

  const colorOptions = [
    '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B',
    '#EF4444', '#EC4899', '#14B8A6', '#6366F1',
  ];

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [active, archived] = await Promise.all([
        chemistryApi.getProjects(),
        chemistryApi.getArchivedProjects(),
      ]);
      setProjects(active);
      setArchivedProjects(archived);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Cleanup undo timer on unmount
  useEffect(() => {
    return () => {
      if (undoRef.current) clearTimeout(undoRef.current.timer);
    };
  }, []);

  const totalCompounds = useMemo(
    () => projects.reduce((sum, p) => sum + (p.contentCounts?.molecules ?? p.moleculeCount ?? 0), 0),
    [projects]
  );

  const handleProjectClick = (project: Project) => {
    navigate(`/chemistry/files?project=${project.id}`);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setIsCreating(true);
    try {
      await chemistryApi.createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
        color: newProjectColor,
      });
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectColor('#10B981');
      setIsCreateModalOpen(false);
      await fetchProjects();
    } catch (err: any) {
      alert(err.message || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchive = useCallback(async (id: number) => {
    const project = projects.find((p) => p.id === id);
    if (!project) return;

    // Optimistic: move from active to archived
    setProjects((prev) => prev.filter((p) => p.id !== id));
    setArchivedProjects((prev) => [{ ...project, archivedAt: new Date().toISOString() }, ...prev]);

    try {
      await chemistryApi.archiveProject(id);
    } catch (err: any) {
      // Revert on failure
      setProjects((prev) => [...prev, project]);
      setArchivedProjects((prev) => prev.filter((p) => p.id !== id));
      showNotification('error', err.message || 'Failed to archive project');
      return;
    }

    // Clear any existing undo
    if (undoRef.current) clearTimeout(undoRef.current.timer);

    const timer = setTimeout(() => {
      setUndoItem(null);
    }, 5000);
    setUndoItem({ project, timer });
    showNotification('success', `"${project.name}" archived`);
  }, [projects, showNotification]);

  const handleUndo = useCallback(async () => {
    if (!undoItem) return;
    clearTimeout(undoItem.timer);
    const { project } = undoItem;
    setUndoItem(null);

    try {
      await chemistryApi.restoreProject(project.id);
      setArchivedProjects((prev) => prev.filter((p) => p.id !== project.id));
      setProjects((prev) => [...prev, { ...project, archivedAt: undefined }]);
      showNotification('success', `"${project.name}" restored`);
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to undo archive');
    }
  }, [undoItem, showNotification]);

  const handleRestore = useCallback(async (id: number) => {
    const project = archivedProjects.find((p) => p.id === id);
    if (!project) return;

    try {
      await chemistryApi.restoreProject(id);
      setArchivedProjects((prev) => prev.filter((p) => p.id !== id));
      setProjects((prev) => [...prev, { ...project, archivedAt: undefined }]);
      showNotification('success', `"${project.name}" restored`);
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to restore project');
    }
  }, [archivedProjects, showNotification]);

  const handlePermanentDelete = useCallback(async () => {
    if (!deleteConfirm || deleteConfirm.confirmText !== deleteConfirm.project.name) return;
    const { project } = deleteConfirm;

    try {
      await chemistryApi.deleteProject(project.id);
      setArchivedProjects((prev) => prev.filter((p) => p.id !== project.id));
      setDeleteConfirm(null);
      showNotification('success', `"${project.name}" permanently deleted`);
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to delete project');
    }
  }, [deleteConfirm, showNotification]);

  const displayProjects = activeTab === 'active' ? projects : archivedProjects;

  return (
    <PageTransition>
      <div className="min-h-screen" style={{ background: 'var(--content-bg)' }}>
        {/* Liquid glass header */}
        <div className="chem-dash__header">
          <div className="chem-dash__header-blobs">
            <div className="chem-dash__blob chem-dash__blob--1" />
            <div className="chem-dash__blob chem-dash__blob--2" />
          </div>
          <div className="chem-dash__header-content">
            <div className="chem-dash__header-left">
              <h1 className="chem-dash__title">Chemistry</h1>
              {!isLoading && !error && (
                <p className="chem-dash__subtitle">
                  {projects.length} program{projects.length !== 1 ? 's' : ''} &middot; {totalCompounds.toLocaleString()} compound placement{totalCompounds !== 1 ? 's' : ''} across projects
                  {archivedProjects.length > 0 && ` \u00b7 ${archivedProjects.length} archived`}
                </p>
              )}
            </div>
            <button
              className="chem-dash__new-btn"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New Program
            </button>
          </div>
        </div>

        {/* Undo toast */}
        {undoItem && (
          <div className="chem-dash__undo-toast">
            <span>"{undoItem.project.name}" archived</span>
            <button onClick={handleUndo} className="chem-dash__undo-btn">Undo</button>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="chem-dash__loading">
            <div className="chem-dash__spinner" />
            <span className="chem-dash__loading-text">Loading projects...</span>
          </div>
        ) : error ? (
          <div className="chem-dash__error">
            <span className="chem-dash__error-msg">{error}</span>
            <button className="chem-dash__retry-btn" onClick={fetchProjects}>
              Retry
            </button>
          </div>
        ) : projects.length === 0 && archivedProjects.length === 0 ? (
          <div className="chem-dash__empty">
            <div className="chem-dash__empty-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
            </div>
            <h2 className="chem-dash__empty-title">No projects yet</h2>
            <p className="chem-dash__empty-desc">
              Create your first chemistry program to start organising compounds, reactions, and data files.
            </p>
            <button
              className="chem-dash__empty-btn"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Create Program
            </button>
          </div>
        ) : (
          <>
            {/* Active / Archived tabs — always visible */}
            <div className="chem-dash__tabs">
              <button
                className={`chem-dash__tab${activeTab === 'active' ? ' chem-dash__tab--active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                Active
                <span className="chem-dash__tab-count">{projects.length}</span>
              </button>
              <button
                className={`chem-dash__tab${activeTab === 'archived' ? ' chem-dash__tab--active' : ''}`}
                onClick={() => setActiveTab('archived')}
              >
                Archived
                <span className="chem-dash__tab-count">{archivedProjects.length}</span>
              </button>
            </div>

            {displayProjects.length === 0 ? (
              <div className="chem-dash__empty">
                <p className="chem-dash__empty-desc" style={{ paddingTop: 40 }}>
                  {activeTab === 'archived' ? 'No archived projects' : 'No active projects'}
                </p>
              </div>
            ) : (
              <div className="chem-dash__grid">
                {displayProjects.map((project) => (
                  <ChemistryProjectCard
                    key={project.id}
                    id={project.id}
                    name={project.name}
                    description={project.description}
                    color={project.color}
                    contentCounts={project.contentCounts}
                    lastModified={project.lastModified}
                    patentRiskCount={project.patentRiskCount}
                    archived={activeTab === 'archived'}
                    onClick={() => handleProjectClick(project)}
                    onArchive={activeTab === 'active' ? handleArchive : undefined}
                    onRestore={activeTab === 'archived' ? handleRestore : undefined}
                    onPermanentDelete={activeTab === 'archived' && isSuperAdmin
                      ? (id) => {
                          const p = archivedProjects.find((ap) => ap.id === id);
                          if (p) setDeleteConfirm({ project: p, confirmText: '' });
                        }
                      : undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Create Project Modal */}
        {isCreateModalOpen && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setIsCreateModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                <h2 className="text-xl font-semibold text-white">Create New Program</h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Program Name</label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Enter program name..."
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Description <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    placeholder="Describe your program..."
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white dark:focus:bg-gray-800 transition-all resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewProjectColor(color)}
                        className={`w-10 h-10 rounded-xl transition-all ${
                          newProjectColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setNewProjectName('');
                    setNewProjectDescription('');
                    setNewProjectColor('#10B981');
                  }}
                  className="px-5 py-2.5 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || isCreating}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
                >
                  {isCreating ? 'Creating...' : 'Create Program'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Permanent Delete Confirmation Modal */}
        {deleteConfirm && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #DC2626, #B91C1C)' }}>
                <h2 className="text-xl font-semibold text-white">Delete Project Forever</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This will permanently delete <strong>"{deleteConfirm.project.name}"</strong> and all its files, molecules, and data. This cannot be undone.
                </p>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Type the project name to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirm.confirmText}
                    onChange={(e) => setDeleteConfirm({ ...deleteConfirm, confirmText: e.target.value })}
                    placeholder={deleteConfirm.project.name}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white dark:focus:bg-gray-800"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handlePermanentDelete()}
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-5 py-2.5 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePermanentDelete}
                  disabled={deleteConfirm.confirmText !== deleteConfirm.project.name}
                  className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default ChemistryDashboard;
